import { z } from "zod";

// =============================================================
// Module local « x-messagerie » (PROTECTO) : modèles de messages SMS et
// courriel, boîte d'envoi, réglages et automatisations (confirmation, rappel
// de la veille, véhicule prêt, avis Google, récapitulatif de fin de journée).
// Types partagés API ↔ web + schémas zod.
// =============================================================

export type MessageChannel = "sms" | "email";

export type TemplateKey =
  | "confirmation"
  | "confirmation_courriel"
  | "rdv_modifie"
  | "rdv_annule_proprio"
  | "rappel_veille"
  | "vehicule_pret"
  | "avis_google"
  | "offre_hiver"
  | "recap_journee"
  | "garantie_emise"
  | "garantie_signature"
  | "garantie_signature_sms"
  | "garantie_signee"
  | "garantie_inspection"
  | "garantie_inspection_retard"
  | "garantie_expiration"
  | "garantie_annulee"
  | "facture"
  | "devis"
  | "site_accuse"
  | "rappel_demande"
  | "libre"
  | `urable.${string}`
  | `suivi.${string}`;

/** Clés dont le modèle existe dans le code (ordre du cycle de vie, pour les listes). */
export const TEMPLATE_KEYS: TemplateKey[] = [
  "confirmation",
  "confirmation_courriel",
  "rdv_modifie",
  "rdv_annule_proprio",
  "rappel_veille",
  "vehicule_pret",
  "avis_google",
  "offre_hiver",
  "facture",
  "devis",
  "garantie_signature",
  "garantie_signature_sms",
  "garantie_signee",
  "garantie_emise",
  "garantie_inspection",
  "garantie_inspection_retard",
  "garantie_expiration",
  "garantie_annulee",
  "site_accuse",
  "rappel_demande",
  "recap_journee",
  "libre",
];

export type OutboxStatus = "en_attente" | "envoye" | "echec" | "simule" | "annule";

export type RelatedType = "appointment" | "warranty" | "client" | "recap" | "call" | "request";

export interface MessageTemplate {
  id: string;
  key: TemplateKey;
  channel: MessageChannel;
  name: string;
  /** Objet du courriel (ignoré pour un SMS). */
  subject: string | null;
  body: string;
  enabled: boolean;
  updatedAt: number | string;
}

export interface MessagerieSettings {
  /** Lien d'avis Google Business. */
  reviewLink: string;
  workshopAddress: string;
  workshopPhone: string;
  /** Heures avant le rendez-vous pour le rappel (24 = la veille à la même heure). */
  reminderHoursBefore: number;
  /** Heure locale (0–23) d'envoi du lien d'avis le jour où le véhicule est prêt. */
  reviewSendHour: number;
  /** Délai après achèvement ; null utilise l'heure fixe. */
  reviewDelayHours?: number | null;
  /** Heure locale (0–23) du récapitulatif de fin de journée. */
  recapHour: number;
  /** Courriel ou téléphone qui reçoit le récapitulatif. */
  recapRecipient: string;
  /**
   * Cellulaire de l'atelier averti par texto à chaque rendez-vous annulé
   * (demande du propriétaire, 22 octobre 2026). Vide : aucun texto.
   */
  cancellationNotifyPhone: string;
  /**
   * Offre de protection de peinture envoyée après la réservation d'un antirouille
   * (demande du propriétaire, 31 octobre 2026), et rabais annoncé, en pour cent.
   */
  winterOfferEnabled: boolean;
  winterOfferDiscountPercent: number;
  senderName: string;
}

export interface OutboxMessage {
  id: string;
  channel: MessageChannel;
  toAddress: string;
  subject: string | null;
  body: string;
  templateKey: TemplateKey | null;
  relatedType: RelatedType | null;
  relatedId: string | null;
  status: OutboxStatus;
  scheduledAt: number | string | null;
  sentAt: number | string | null;
  providerId: string | null;
  error: string | null;
  createdAt: number | string;
}

export interface OutboxSummary {
  sentToday: number;
  pending: number;
  failed: number;
  /** Canaux réellement branchés (clés présentes) ; sinon les envois sont simulés. */
  smsConfigured: boolean;
  emailConfigured: boolean;
  /** Adresse d'expédition des courriels (MAILJET_FROM_EMAIL), null si absente. */
  emailSender?: string | null;
}

export interface TemplatePreview {
  channel: MessageChannel;
  to: string | null;
  /** Nom du client de l'aperçu (exemple ou rendez-vous choisi), pour l'en-tête. */
  toName?: string | null;
  subject: string | null;
  body: string;
}

/**
 * Charge utile attendue des événements « atelier.appointment.created » et
 * « atelier.appointment.status_changed » (émis par x-atelier, consommés ici).
 */
export interface AppointmentEventPayload {
  id: string;
  clientId: string;
  clientName: string;
  clientPhone: string | null;
  clientEmail: string | null;
  vehicleLabel: string | null;
  startAt: string;
  endAt: string;
  status: string;
  previousStatus?: string;
  title: string;
  totalCents: number;
  technicianName: string | null;
}

/**
 * Charge utile de « atelier.appointment.rescheduled » : l'horaire d'un
 * rendez-vous a changé (début déplacé). `notifyClient` : faux si l'employé a
 * choisi de ne pas prévenir le client (déjà averti autrement).
 */
export interface AppointmentRescheduledPayload extends AppointmentEventPayload {
  previousStartAt: string;
  previousEndAt: string;
  notifyClient: boolean;
}

/** Charge utile de « garanties.warranty.emitted » et « garanties.warranty.cancelled » (module garanties). */
export interface WarrantyEventPayload {
  id: string;
  number: string;
  clientId: string;
  phone?: string | null;
  email?: string | null;
}

/** Destinataire d'un courriel de test envoyé depuis les réglages (admin). */
export const testEmailSchema = z.object({
  to: z.string().trim().email(),
});

// ---- Libellés ----

export const CHANNEL_LABELS: Record<MessageChannel, string> = {
  sms: "SMS",
  email: "Courriel",
};

export const OUTBOX_STATUS_LABELS: Record<OutboxStatus, string> = {
  en_attente: "En attente",
  envoye: "Envoyé",
  echec: "Échec",
  simule: "Simulé",
  annule: "Annulé",
};

export const TEMPLATE_KEY_LABELS: Partial<Record<TemplateKey, string>> = {
  confirmation: "Confirmation de réservation",
  confirmation_courriel: "Confirmation détaillée par courriel",
  rdv_modifie: "Rendez-vous déplacé",
  rdv_annule_proprio: "Rendez-vous annulé (avis à l'atelier)",
  rappel_veille: "Rappel de la veille",
  vehicule_pret: "Véhicule prêt",
  avis_google: "Lien d'avis Google",
  recap_journee: "Récapitulatif de fin de journée",
  facture: "Facture envoyée par courriel",
  devis: "Devis envoyé par courriel",
  garantie_emise: "Garantie émise",
  garantie_signature: "Garantie à signer",
  garantie_signature_sms: "Garantie à signer par texto",
  garantie_signee: "Copie de garantie signée",
  garantie_inspection: "Inspection de garantie à venir",
  garantie_inspection_retard: "Inspection de garantie en retard",
  garantie_expiration: "Garantie bientôt expirée",
  garantie_annulee: "Garantie annulée",
  site_accuse: "Accusé de réception du site web",
  rappel_demande: "Demande de rappel reçue",
  libre: "Message libre",
};

/** Libellé d'une clé de modèle, y compris les clés dynamiques (suivis, import). */
export function templateKeyLabel(key: TemplateKey | string | null): string {
  if (!key) return "—";
  const known = TEMPLATE_KEY_LABELS[key as TemplateKey];
  if (known) return known;
  if (key.startsWith("suivi.")) return "Rappel de suivi";
  if (key.startsWith("urable.")) return "Message Urable";
  return key;
}

export const RELATED_TYPE_LABELS: Record<RelatedType, string> = {
  appointment: "Rendez-vous",
  warranty: "Garantie",
  client: "Client",
  recap: "Récapitulatif",
  call: "Appel",
  request: "Demande (rappel ou annulation)",
};

/** Variables disponibles dans les modèles, avec leur portée. */
export interface TemplateVariable {
  name: string;
  description: string;
  /** Modèles où la variable a un sens (vide = tous). */
  only?: TemplateKey[];
}

/** Modèles rendus avec toutes les variables d'un rendez-vous (date, heure, montants…). */
const APPOINTMENT_TEMPLATES: TemplateKey[] = [
  "confirmation",
  "confirmation_courriel",
  "rdv_modifie",
  "rdv_annule_proprio",
  "rappel_veille",
  "vehicule_pret",
  "avis_google",
  "offre_hiver",
];
/** Modèles de garantie rendus avec le véhicule et le service de la garantie. */
const WARRANTY_VEHICLE_TEMPLATES: TemplateKey[] = [
  "garantie_signature",
  "garantie_signature_sms",
  "garantie_signee",
  "garantie_inspection",
  "garantie_inspection_retard",
  "garantie_expiration",
  "garantie_annulee",
];
/** Modèles qui connaissent le véhicule et le service (rendez-vous, documents, garanties). */
const VEHICLE_TEMPLATES: TemplateKey[] = [
  ...APPOINTMENT_TEMPLATES,
  "facture",
  "devis",
  ...WARRANTY_VEHICLE_TEMPLATES,
];

/**
 * Variables des modèles. `only` restreint l'aide de l'éditeur aux modèles qui reçoivent
 * vraiment la variable (constat du 26 octobre 2026 : l'atelier essayait le véhicule et
 * le lien de gestion dans des modèles qui ne les ont pas, ou les recevaient vides).
 */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  {
    name: "clientPrenom",
    description: "Prénom du client (premier mot de son nom, remis en majuscule initiale)",
  },
  { name: "clientNom", description: "Nom complet du client, tel que saisi" },
  {
    name: "clientTelephone",
    description: "Téléphone du client, lisible (ex. 418 555 0134 ; vide s'il est inconnu)",
    only: ["rdv_annule_proprio"],
  },
  {
    name: "vehicule",
    description:
      "Véhicule du rendez-vous (année, marque, modèle) ; « véhicule » si aucun véhicule n'est rattaché au rendez-vous",
    only: VEHICLE_TEMPLATES,
  },
  {
    name: "date",
    description: "Date du rendez-vous (ex. mardi 15 avril)",
    only: [...APPOINTMENT_TEMPLATES, "recap_journee"],
  },
  { name: "heure", description: "Heure du rendez-vous (ex. 9 h 00)", only: APPOINTMENT_TEMPLATES },
  {
    name: "service",
    description: "Titre du rendez-vous ou service principal",
    only: VEHICLE_TEMPLATES,
  },
  { name: "montantAcompte", description: "Acompte du rendez-vous", only: APPOINTMENT_TEMPLATES },
  {
    name: "montantDu",
    description: "Montant restant dû (total − payé)",
    only: APPOINTMENT_TEMPLATES,
  },
  { name: "adresseAtelier", description: "Adresse de l'atelier (réglages)" },
  { name: "telephoneAtelier", description: "Téléphone de l'atelier (réglages)" },
  {
    name: "rabaisOffre",
    description: "Rabais de l'offre après un antirouille (réglages, ex. 30 %)",
    only: ["offre_hiver"],
  },
  {
    name: "motifRappel",
    description: "Motif d'une demande de rappel, entre parenthèses (vide sinon)",
    only: ["rappel_demande"],
  },
  { name: "nomAtelier", description: "Nom d'expéditeur (réglages)" },
  { name: "lienAvis", description: "Lien d'avis Google (réglages)" },
  {
    name: "numeroGarantie",
    description: "Numéro de la garantie",
    only: [
      "garantie_emise",
      "garantie_signature",
      "garantie_signature_sms",
      "garantie_signee",
      "garantie_inspection",
      "garantie_inspection_retard",
      "garantie_expiration",
      "garantie_annulee",
    ],
  },
  {
    name: "lienSignature",
    description: "Lien de signature électronique de la garantie",
    only: ["garantie_signature", "garantie_signature_sms"],
  },
  {
    name: "dateInspection",
    description: "Date à laquelle l'inspection de la garantie est due (ex. 15 avril 2028)",
    only: ["garantie_inspection", "garantie_inspection_retard"],
  },
  {
    name: "dateLimite",
    description: "Fin de la période de grâce de l'inspection (30 jours après l'échéance)",
    only: ["garantie_inspection", "garantie_inspection_retard"],
  },
  {
    name: "dateExpiration",
    description: "Fin de couverture de la garantie",
    only: ["garantie_expiration", "garantie_inspection", "garantie_inspection_retard"],
  },
  {
    name: "numeroDocument",
    description: "Numéro de la facture ou du devis",
    only: ["facture", "devis"],
  },
  {
    name: "montantTotal",
    description: "Total taxes comprises de la facture ou du devis",
    only: ["facture", "devis"],
  },
  { name: "rdvDemain", description: "Liste des rendez-vous de demain", only: ["recap_journee"] },
  { name: "vehiculesPrets", description: "Véhicules prêts à récupérer", only: ["recap_journee"] },
  { name: "encaissements", description: "Encaissements du jour", only: ["recap_journee"] },
];

/**
 * Variables offertes à l'édition d'un modèle. Les clés dynamiques (rappels de suivi,
 * modèles importés d'Urable) parlent toujours d'un rendez-vous.
 */
export function templateVariablesFor(key: TemplateKey | string): TemplateVariable[] {
  const effective = /^(?:urable|suivi)\./.test(key) ? "confirmation" : (key as TemplateKey);
  return TEMPLATE_VARIABLES.filter((v) => !v.only || v.only.includes(effective));
}

// ---- Schémas ----

export const messageChannelSchema = z.enum(["sms", "email"]);
export const templateKeySchema = z.union([
  z.enum([
    "confirmation",
    "confirmation_courriel",
    "rdv_modifie",
    "rdv_annule_proprio",
    "rappel_veille",
    "vehicule_pret",
    "avis_google",
    "recap_journee",
    "facture",
    "devis",
    "garantie_emise",
    "garantie_signature",
    "garantie_signature_sms",
    "garantie_signee",
    "garantie_inspection",
    "garantie_inspection_retard",
    "garantie_expiration",
    "garantie_annulee",
    "site_accuse",
    "rappel_demande",
    "libre",
  ]),
  z.string().regex(/^urable\.[\w.-]+$/),
  z.string().regex(/^suivi\.[\w.-]+$/),
]);
export const outboxStatusSchema = z.enum(["en_attente", "envoye", "echec", "simule", "annule"]);

const hour = z.number().int().min(0).max(23);

/** Numéro nord-américain à dix chiffres (avec ou sans le 1), saisi librement, ou vide. */
const phoneOrEmpty = z
  .string()
  .trim()
  .max(40)
  .refine(
    (value) => {
      if (!value) return true;
      const digits = value.replace(/\D/g, "");
      return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
    },
    { message: "numéro de téléphone à dix chiffres attendu" },
  );

export const settingsSchema = z.object({
  reviewLink: z.union([z.string().trim().url(), z.literal("")]).default(""),
  workshopAddress: z.string().trim().max(300).default(""),
  workshopPhone: z.string().trim().max(40).default(""),
  reminderHoursBefore: z.number().int().min(1).max(168).default(24),
  reviewSendHour: hour.default(19),
  reviewDelayHours: z.number().min(0).max(168).nullable().default(null),
  recapHour: hour.default(18),
  recapRecipient: z.string().trim().max(200).default(""),
  cancellationNotifyPhone: phoneOrEmpty.default(""),
  winterOfferEnabled: z.boolean().default(true),
  winterOfferDiscountPercent: z.number().int().min(1).max(90).default(30),
  senderName: z.string().trim().min(1).max(60).default("PROTECTO"),
});
export const updateSettingsSchema = settingsSchema.partial();
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;

export const updateTemplateSchema = z.object({
  channel: messageChannelSchema.optional(),
  name: z.string().trim().min(1).max(120).optional(),
  subject: z.string().trim().max(200).nullable().optional(),
  body: z.string().max(5000).optional(),
  enabled: z.boolean().optional(),
});
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>;

export const previewSchema = z.object({
  appointmentId: z.string().min(1).optional(),
});

/** Envoi d'essai d'un modèle : cellulaire ou courriel, rendu sur l'exemple ou un rendez-vous. */
export const testTemplateSchema = z.object({
  to: z.string().trim().min(3).max(200),
  appointmentId: z.string().min(1).optional(),
});

/** Message libre : un destinataire explicite OU un client (téléphone/courriel du client). */
export const createOutboxSchema = z
  .object({
    channel: messageChannelSchema,
    to: z.string().trim().min(1).max(200).optional(),
    clientId: z.string().min(1).optional(),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().min(1).max(5000),
    relatedType: z.enum(["appointment", "warranty", "client"]).optional(),
    relatedId: z.string().min(1).optional(),
  })
  .refine((v) => Boolean(v.to || v.clientId), {
    message: "destinataire ou client requis",
    path: ["to"],
  });
export type CreateOutboxInput = z.infer<typeof createOutboxSchema>;

export const outboxQuerySchema = z.object({
  status: outboxStatusSchema.optional(),
  channel: messageChannelSchema.optional(),
  q: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

// ---- Rappels de suivi par service ----

/**
 * Un rappel de suivi part un certain temps après l'achèvement d'un travail
 * qui contient le service (« inspection de l'antirouille aux deux ans »,
 * « scellant à refaire dans 19 semaines »). Il peut se répéter : quatre
 * répétitions aux deux ans couvrent une garantie de dix ans. Le rappel vaut
 * pour toutes les déclinaisons du service (même groupe de variantes).
 */
export type FollowupUnit = "day" | "week" | "month" | "year";
export type FollowupChannel = MessageChannel | "both";

export const FOLLOWUP_UNIT_LABELS: Record<FollowupUnit, [singular: string, plural: string]> = {
  day: ["jour", "jours"],
  week: ["semaine", "semaines"],
  month: ["mois", "mois"],
  year: ["an", "ans"],
};

export const FOLLOWUP_CHANNEL_LABELS: Record<FollowupChannel, string> = {
  sms: "SMS",
  email: "Courriel",
  both: "SMS et courriel",
};

/** Répétitions maximales d'un même rappel (en plus du premier envoi). */
export const FOLLOWUP_MAX_REPEATS = 9;

export interface ServiceFollowup {
  id: string;
  /** Service (ou l'une de ses déclinaisons) qui déclenche le rappel. */
  serviceId: string;
  /** Nom court affiché dans la liste (« Inspection antirouille »). */
  label: string;
  /** Délai après l'achèvement : `delayValue` × `delayUnit` (1,5 an accepté). */
  delayValue: number;
  delayUnit: FollowupUnit;
  /** Envois supplémentaires au même intervalle (0 = une seule fois). */
  repeatCount: number;
  channel: FollowupChannel;
  /** Objet du courriel (ignoré pour un SMS) ; vide = le nom du rappel. */
  subject: string | null;
  body: string;
  enabled: boolean;
  /** Clé du rappel importé d'Urable dont il provient, le cas échéant. */
  sourceKey: string | null;
  updatedAt: number | string;
}

/** « 2 ans », « 1,5 an », « 19 semaines ». */
export function formatFollowupDelay(value: number, unit: FollowupUnit): string {
  const [singular, plural] = FOLLOWUP_UNIT_LABELS[unit];
  const n = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2 }).format(value);
  return `${n} ${value > 1 ? plural : singular}`;
}

/** Échéances d'un rappel : 1 + répétitions, en multiples du délai. */
export function followupOccurrences(
  f: Pick<ServiceFollowup, "delayValue" | "delayUnit" | "repeatCount">,
): Array<{ n: number; value: number; unit: FollowupUnit }> {
  const total = 1 + Math.max(0, Math.min(FOLLOWUP_MAX_REPEATS, f.repeatCount));
  return Array.from({ length: total }, (_, i) => ({
    n: i + 1,
    value: f.delayValue * (i + 1),
    unit: f.delayUnit,
  }));
}

export const followupUnitSchema = z.enum(["day", "week", "month", "year"]);
export const followupChannelSchema = z.enum(["sms", "email", "both"]);

export const createFollowupSchema = z.object({
  serviceId: z.string().min(1),
  label: z.string().trim().min(1).max(120),
  delayValue: z.number().positive().max(120),
  delayUnit: followupUnitSchema,
  repeatCount: z.number().int().min(0).max(FOLLOWUP_MAX_REPEATS).default(0),
  channel: followupChannelSchema.default("sms"),
  subject: z.string().trim().max(200).nullable().optional(),
  body: z.string().trim().min(1).max(5000),
  enabled: z.boolean().default(true),
});
export type CreateFollowupInput = z.infer<typeof createFollowupSchema>;
export const updateFollowupSchema = createFollowupSchema.partial();
export type UpdateFollowupInput = z.infer<typeof updateFollowupSchema>;

export const followupPreviewSchema = z.object({
  channel: followupChannelSchema.default("sms"),
  subject: z.string().trim().max(200).nullable().optional(),
  body: z.string().max(5000),
});
